import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {
    AccountTree as AccountIcon,
    Business as ClientIcon,
    ChevronRight,
    Edit as EditIcon,
    ExpandMore,
    Layers as GroupIcon,
    Lock as LockIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import { TabPanel } from "@mui/lab";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { getBillingRetainers, saveBillingRetainerTerms } from "../redux/ducks/billingRetainers";
import { searchBillingEntities } from "../redux/ducks/billingEntities";
import { getAccountGroups } from "../redux/ducks/account";

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: theme.spacing(3, 0, 0, 0)
}));

const StatsLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.md,
    marginBottom: theme.spacing(2)
}));

const TreeContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.paper,
    overflow: "hidden"
}));

const NodeRow = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "$depth"
})(({ theme, $depth }) => ({
    paddingLeft: theme.spacing(2 + $depth * 2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderLeft: "3px solid transparent",
    cursor: "default",
    "&:hover": {
        backgroundColor: theme.palette.action.hover
    }
}));

const ChevronIcon = styled(ListItemIcon)({
    minWidth: 28,
    cursor: "pointer"
});

const NarrowIcon = styled(ListItemIcon)({
    minWidth: 28
});

const SpacerBox = styled(Box)({
    width: 20
});

const NodeLabelRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1)
}));

const NodeName = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$strong",
})(({ theme, $strong }) => ({
    fontWeight: $strong ? theme.typography.fontWeightSemiBold : theme.typography.fontWeightMedium,
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    textAlign: "center",
    padding: theme.spacing(1, 0),
}));

const SelectedEntityBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const TermsLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(8),
    textAlign: "center",
    color: theme.palette.text.secondary
}));

const BillingEntityNote = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
}));

const OverageRateBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(5),
    marginBottom: theme.spacing(1)
}));

const InvoiceWordingBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2)
}));

const InvoiceWordingHint = styled(Typography)(({ theme }) => ({
    display: "block",
    marginBottom: theme.spacing(0.5)
}));

const PathLine = styled(Typography)(({ theme }) => ({
    display: "block",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
}));

const StepHint = styled(Typography)(({ theme }) => ({
    display: "block",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
}));

const EmptyHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 0)
}));

const CHILD_KEY = { Client: "accounts", Account: "groups" };
const CHILD_TYPE = { Client: "Account", Account: "Group" };
const TYPE_ICON = { Client: ClientIcon, Account: AccountIcon, Group: GroupIcon };
const TYPE_CHIP_COLOR = { Client: "primary", Account: "secondary", Group: "info" };
const TYPE_HINT = {
    Client: "Client. Its retainer pool covers every account underneath it.",
    Account: "Account. Its retainer pool covers the work booked to this account.",
    Group: "Group. Its retainer pool covers only the projects inside this group, and it is invoiced separately from its account."
};

const SKILL_OVERAGE_RATE = "Skill Rate";
const RETAINER_OVERAGE_RATE = "Retainer Rate";
const CUSTOM_OVERAGE_RATE = "Custom Rate";
const OVERAGE_RATE_MODES = [SKILL_OVERAGE_RATE, RETAINER_OVERAGE_RATE, CUSTOM_OVERAGE_RATE];
const DEFAULT_OVERAGE_RATE_MODE = SKILL_OVERAGE_RATE;

const formatPath = (path) => path.filter(Boolean).join(" › ");

const formatCurrency = (n) =>
    typeof n === "number"
        ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
        : "";

const formatRate = (n) => Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const chosenOverageRateMode = (terms) => {
    const mode = terms?.overageRate?.mode;
    return OVERAGE_RATE_MODES.includes(mode) ? mode : "";
};

const overageRateModeOf = (terms) => chosenOverageRateMode(terms) || DEFAULT_OVERAGE_RATE_MODE;

const needsOverageRate = (terms) => terms?.type === "Retainer" && !!terms?.overage && !chosenOverageRateMode(terms);

const countNeedsOverageRate = (node, entityType) => {
    const children = node?.[CHILD_KEY[entityType]] || [];
    return children.reduce((n, child) => n + countNeedsOverageRate(child, CHILD_TYPE[entityType]) + (needsOverageRate(child.billingTerms) ? 1 : 0), 0);
};

const impliedRetainerRate = (terms) => {
    const hours = Number(terms?.hours);
    const charge = Number(terms?.charge);
    if (!Number.isFinite(hours) || hours <= 0) return null;
    if (!Number.isFinite(charge) || charge <= 0) return null;
    return charge / hours;
};

const hasCustomOverageRateAmount = (terms) => {
    const amount = Number(terms?.overageRate?.amount);
    return Number.isFinite(amount) && amount > 0;
};

const termsForSave = (terms) => {
    if (!terms || !("overageRate" in terms)) return terms;
    const usable = terms.type === "Retainer" && (overageRateModeOf(terms) !== CUSTOM_OVERAGE_RATE || hasCustomOverageRateAmount(terms));
    if (usable) return terms;
    const rest = { ...terms };
    delete rest.overageRate;
    return rest;
};

const customOverageRateAmountError = (terms) => {
    if (terms?.type !== "Retainer" || !terms?.overage) return null;
    if (overageRateModeOf(terms) !== CUSTOM_OVERAGE_RATE) return null;
    if (hasCustomOverageRateAmount(terms)) return null;
    return "Enter the hourly amount for the custom overage rate.";
};

const summarizeTerms = (terms) => {
    if (!terms) return "—";
    const t = terms.type;
    if (t === "Retainer") {
        const hours = terms.hours ? `${terms.hours}h` : null;
        const charge = terms.charge ? `${formatCurrency(terms.charge)}/mo` : null;
        return ["Retainer", hours, charge].filter(Boolean).join(" · ");
    }
    if (t === "Fixed Price") return "Fixed Price";
    if (t === "Hourly") return "Hourly";
    if (t === "Internal") return "Internal";
    if (t === "Non Billable") return "Non Billable";
    if (t === "Parent") return "Parent (inherits)";
    return t || "—";
};

const BillingTermsForm = ({ values, onChange, poolName }) => {
    const label = (poolName || "").trim();
    const hours = Number(values?.hours);
    const hasHours = Number.isFinite(hours) && hours > 0;
    const retainerLinePlaceholder = label && hasHours ? `${label} Retainer ${hours}Hrs.` : "Built from the pool name and hours when the invoice is generated";
    const overageLinePlaceholder = label ? `${label} Retainer Overage` : "Built from the pool name when the invoice is generated";

    const overageOn = !!values?.overage;
    const overageRateMode = chosenOverageRateMode(values);
    const impliedRate = impliedRetainerRate(values);
    const customAmount = values?.overageRate?.amount;
    const customAmountEntered = customAmount !== null && customAmount !== undefined && customAmount !== "";
    const customAmountError = customOverageRateAmountError(values);

    const setOverageRateMode = (mode) => onChange("overageRate", { ...(values?.overageRate || {}), mode });
    const setCustomOverageRate = (amount) => onChange("overageRate", { ...(values?.overageRate || {}), mode: CUSTOM_OVERAGE_RATE, amount });

    let overageRateHelper;
    if (overageOn && !overageRateMode) {
        overageRateHelper =
            "No rate has been chosen yet. Until someone picks one, hours past the pool are charged at the retainer rate set for each skill. Pick an option to confirm how this retainer should bill.";
    } else if (!overageOn) {
        overageRateHelper = `Overage billing is off, so hours past the ${hasHours ? `${hours} in the pool` : "pool"} are not charged at all and this rate does nothing. Turn on Overage billing above to use it.`;
    } else if (overageRateMode === RETAINER_OVERAGE_RATE) {
        overageRateHelper = impliedRate
            ? `Every hour past the pool is charged at ${formatRate(impliedRate)} — this retainer's own hourly rate, ${formatCurrency(Number(values?.charge))} divided by ${hours} hours.`
            : "Fill in the hours and the charge above first. Without both, this rate cannot be worked out and extra hours will be held back off the invoice instead of billed.";
    } else if (overageRateMode === CUSTOM_OVERAGE_RATE) {
        overageRateHelper = "Every hour past the pool is charged at the amount you enter below, whatever the retainer or the skills would otherwise work out to.";
    } else {
        overageRateHelper =
            "Each hour past the pool is charged at the retainer rate set for the skill it was logged under, so different work can be charged at different rates. This is what the system has always done.";
    }

    return (
        <Box>
            <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Type</InputLabel>
                <Select
                    label="Type"
                    value={values?.type || "Retainer"}
                    onChange={(e) => onChange("type", e.target.value)}
                >
                    <MenuItem value="Retainer">Retainer</MenuItem>
                    <MenuItem value="Hourly">Hourly</MenuItem>
                    <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                    <MenuItem value="Internal">Internal</MenuItem>
                    <MenuItem value="Non Billable">Non Billable</MenuItem>
                    <MenuItem value="Parent">Parent</MenuItem>
                </Select>
            </FormControl>

            {values?.type === "Retainer" && (
                <>
                    <FormControl fullWidth size="small" margin="dense">
                        <InputLabel>Frequency</InputLabel>
                        <Select
                            label="Frequency"
                            value={values?.frequency || "Monthly"}
                            onChange={(e) => onChange("frequency", e.target.value)}
                        >
                            <MenuItem value="Monthly">Monthly</MenuItem>
                            <MenuItem value="Weekly">Weekly</MenuItem>
                            <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                            <MenuItem value="Quarterly">Quarterly</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label="Hours"
                        type="number"
                        value={values?.hours ?? ""}
                        onChange={(e) => onChange("hours", e.target.value === "" ? null : Number(e.target.value))}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label="Monthly Charge"
                        type="number"
                        value={values?.charge ?? ""}
                        onChange={(e) => onChange("charge", e.target.value === "" ? null : Number(e.target.value))}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!values?.overage}
                                onChange={(e) => onChange("overage", e.target.checked)}
                                size="small"
                            />
                        }
                        label="Overage billing"
                    />
                    <OverageRateBox>
                        <FormControl fullWidth size="small" margin="dense" disabled={!overageOn}>
                            <InputLabel shrink>Rate for hours past the retainer</InputLabel>
                            <Select label="Rate for hours past the retainer" notched displayEmpty value={overageRateMode} onChange={(e) => setOverageRateMode(e.target.value)}>
                                <MenuItem value="" disabled>
                                    Not chosen yet
                                </MenuItem>
                                <MenuItem value={SKILL_OVERAGE_RATE}>The rate for each skill</MenuItem>
                                <MenuItem value={RETAINER_OVERAGE_RATE}>
                                    {impliedRate ? `This retainer's own rate — ${formatRate(impliedRate)}/hour` : "This retainer's own rate — charge divided by hours"}
                                </MenuItem>
                                <MenuItem value={CUSTOM_OVERAGE_RATE}>A rate I set here</MenuItem>
                            </Select>
                            <FormHelperText>{overageRateHelper}</FormHelperText>
                        </FormControl>
                        {overageRateMode === CUSTOM_OVERAGE_RATE && (
                            <TextField
                                fullWidth
                                required
                                size="small"
                                margin="dense"
                                disabled={!overageOn}
                                label="Charge per extra hour"
                                type="number"
                                value={customAmountEntered ? customAmount : ""}
                                onChange={(e) => setCustomOverageRate(e.target.value === "" ? null : Number(e.target.value))}
                                error={customAmountEntered && !!customAmountError}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                helperText={
                                    customAmountEntered && customAmountError
                                        ? customAmountError
                                        : impliedRate
                                          ? `Charged for every hour past the retainer. For comparison, this retainer works out to ${formatRate(impliedRate)}/hour.`
                                          : "Charged for every hour past the retainer."
                                }
                            />
                        )}
                    </OverageRateBox>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!values?.vip}
                                onChange={(e) => onChange("vip", e.target.checked)}
                                size="small"
                            />
                        }
                        label="VIP client"
                    />
                    <InvoiceWordingBox>
                        <Typography variant="subtitle2">Invoice wording</Typography>
                        <InvoiceWordingHint variant="caption" color="text.secondary">
                            These names are printed word for word on the invoice, except for month tokens:{" "}
                            {"{PERIOD}"} is the month billed, {"{PERIOD+1}"} the one after, {"{PERIOD-1}"} the one before, each printed as "September 2026"
                            with the year following the month across January. Leave a field empty to use the greyed-out example, which is rebuilt from the
                            pool name and hours every time an invoice is generated.
                        </InvoiceWordingHint>
                        <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Retainer line name"
                            value={values?.retainerLineName || ""}
                            placeholder={retainerLinePlaceholder}
                            onChange={(e) => onChange("retainerLineName", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText="Description of the monthly retainer charge itself. {PERIOD+1} prints the next month, e.g. September 2026."
                        />
                        <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Overage line name"
                            value={values?.overageLineName || ""}
                            placeholder={overageLinePlaceholder}
                            onChange={(e) => onChange("overageLineName", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText="Description of the hours billed beyond the retainer. {PERIOD} prints the month billed. Only used when overage billing is on. If those hours were worked at more than one rate, the rate is added to the end of each line."
                        />
                    </InvoiceWordingBox>
                </>
            )}

            <BillingEntityNote>
                <LockIcon fontSize="small" color="disabled" />
                <Typography variant="caption" color="text.secondary">
                    This retainer will always be billed as a billing entity.
                </Typography>
            </BillingEntityNote>
        </Box>
    );
};

const EditRetainerDialog = ({ open, entityType, node, path = [], onClose, onSaved }) => {
    const dispatch = useDispatch();
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && node) {
            setValues(node.billingTerms ? { ...node.billingTerms } : { type: "Retainer" });
        }
    }, [open, node]);

    const handleSave = () => {
        setSaving(true);
        dispatch(
            saveBillingRetainerTerms(entityType, node._id, termsForSave(values), (resp) => {
                setSaving(false);
                if (resp?.ok) {
                    onSaved();
                    onClose();
                }
            })
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Retainer Terms — {node?.name} ({entityType})
            </DialogTitle>
            <DialogContent>
                <PathLine variant="caption">
                    {path.length > 0 ? `${formatPath([...path, node?.name])} · ` : ""}
                    Saving replaces the retainer terms on this {(entityType || "entity").toLowerCase()} and applies from the next billing run. Hours already invoiced are not changed.
                </PathLine>
                <BillingTermsForm
                    values={values}
                    poolName={node?.name}
                    onChange={(field, val) => setValues((prev) => ({ ...prev, [field]: val }))}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || !!customOverageRateAmountError(values)}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const AddRetainerDialog = ({ open, entityType, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchDone, setSearchDone] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [groups, setGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [values, setValues] = useState({ type: "Retainer", billingEntity: true });
    const [saving, setSaving] = useState(false);
    const debounceRef = useRef(null);

    const searchesAccounts = entityType === "Account" || entityType === "Group";

    useEffect(() => {
        if (!open) {
            setSearchTerm("");
            setSearchResults([]);
            setSearching(false);
            setSearchDone(false);
            setSelectedAccount(null);
            setGroups([]);
            setLoadingGroups(false);
            setSelectedEntity(null);
            setValues({ type: "Retainer", billingEntity: true });
            setSaving(false);
        }
    }, [open]);

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!term.trim()) {
            setSearchResults([]);
            setSearchDone(false);
            return;
        }
        debounceRef.current = setTimeout(() => {
            setSearching(true);
            dispatch(
                searchBillingEntities(term.trim(), false, (resp) => {
                    setSearching(false);
                    setSearchDone(true);
                    if (!resp) {
                        setSearchResults([]);
                        return;
                    }
                    if (searchesAccounts) {
                        const accounts = [];
                        for (const client of resp.clients || []) {
                            for (const acc of client.accounts || []) {
                                accounts.push({ ...acc, _clientName: client.name });
                            }
                        }
                        setSearchResults(accounts);
                    } else {
                        setSearchResults(resp.clients || []);
                    }
                })
            );
        }, 400);
    };

    const handleSelectAccount = (account) => {
        setSelectedAccount(account);
        setSearchResults([]);
        setSearchTerm("");
        setSearchDone(false);
        setGroups([]);
        setLoadingGroups(true);
        dispatch(
            getAccountGroups(account._id, (resp) => {
                setLoadingGroups(false);
                setGroups(Array.isArray(resp) ? resp.filter((group) => !group.isDeleted) : []);
            })
        );
    };

    const chooseEntity = (entity) => {
        setSelectedEntity(entity);
        setValues(entity.billingTerms?.type === "Retainer" ? { ...entity.billingTerms, type: "Retainer" } : { type: "Retainer", billingEntity: true });
    };

    const handleSelectSearchResult = (entity) => {
        if (entityType === "Group") {
            handleSelectAccount(entity);
            return;
        }
        chooseEntity(entity);
        setSearchResults([]);
        setSearchTerm("");
        setSearchDone(false);
    };

    const handleClearSelection = () => setSelectedEntity(null);

    const handleSave = () => {
        if (!selectedEntity) return;
        setSaving(true);
        dispatch(
            saveBillingRetainerTerms(entityType, selectedEntity._id, termsForSave(values), (resp) => {
                setSaving(false);
                if (resp?.ok) {
                    onSaved();
                    onClose();
                }
            })
        );
    };

    const searchPlaceholder = searchesAccounts ? "Search accounts by account or client name…" : "Search clients by name…";
    const searchHint =
        entityType === "Group"
            ? "Step 1 of 2. Find the account the group sits under, then pick the group."
            : `Find the ${(entityType || "entity").toLowerCase()} you want to put on a retainer.`;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Retainer — {entityType}</DialogTitle>
            <DialogContent>
                {!selectedEntity && !selectedAccount && (
                    <>
                        <StepHint variant="caption">{searchHint}</StepHint>
                        <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        {searching && (
                            <LoadingRow>
                                <CircularProgress size={18} />
                            </LoadingRow>
                        )}
                        {!searching && searchDone && searchResults.length === 0 && (
                            <EmptyHint variant="body2">Nothing matched that search. Try part of the client or account name.</EmptyHint>
                        )}
                        {searchResults.length > 0 && (
                            <List dense>
                                {searchResults.map((entity) => (
                                    <ListItemButton key={entity._id} onClick={() => handleSelectSearchResult(entity)}>
                                        <ListItemText
                                            primary={entity.name}
                                            secondary={entity._clientName ? `Client: ${entity._clientName}` : null}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </>
                )}
                {entityType === "Group" && selectedAccount && !selectedEntity && (
                    <>
                        <SelectedEntityBox>
                            <Chip label={`Account: ${selectedAccount.name}`} onDelete={() => setSelectedAccount(null)} />
                        </SelectedEntityBox>
                        <StepHint variant="caption">
                            Step 2 of 2. Pick the group to put on a retainer. A group retainer covers only the projects inside that group, and it is invoiced
                            separately from its account.
                        </StepHint>
                        {loadingGroups ? (
                            <LoadingRow>
                                <CircularProgress size={18} />
                            </LoadingRow>
                        ) : groups.length === 0 ? (
                            <EmptyHint variant="body2">This account has no groups. Create the group first, then come back to set its retainer.</EmptyHint>
                        ) : (
                            <List dense>
                                {groups.map((group) => (
                                    <ListItemButton key={group._id} onClick={() => chooseEntity(group)}>
                                        <ListItemText primary={group.name} secondary={`Currently ${summarizeTerms(group.billingTerms)}`} />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </>
                )}
                {selectedEntity && (
                    <>
                        <SelectedEntityBox>
                            <Chip label={`${entityType}: ${selectedEntity.name}`} onDelete={handleClearSelection} />
                        </SelectedEntityBox>
                        {entityType === "Group" && selectedAccount && (
                            <PathLine variant="caption">
                                {formatPath([selectedAccount._clientName, selectedAccount.name, selectedEntity.name])}
                            </PathLine>
                        )}
                        <BillingTermsForm
                            values={values}
                            poolName={selectedEntity.name}
                            onChange={(field, val) => setValues((prev) => ({ ...prev, [field]: val }))}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || !selectedEntity || !!customOverageRateAmountError(values)}>
                    {saving ? "Saving…" : `Save ${(entityType || "").toLowerCase()} retainer`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const RetainerRow = ({ entityType, node, depth, path, expandedIds, onToggleExpand, onEdit }) => {
    const childType = CHILD_TYPE[entityType];
    const children = (childType && node[CHILD_KEY[entityType]]) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(node._id);
    const EntityIcon = TYPE_ICON[entityType] || AccountIcon;
    const childPath = [...path, node.name];
    const rateUnset = needsOverageRate(node.billingTerms);
    const hiddenRateUnset = isExpanded ? 0 : countNeedsOverageRate(node, entityType);

    return (
        <>
            <NodeRow
                $depth={depth}
                disableRipple
                onClick={(e) => {
                    if (hasChildren) {
                        e.preventDefault();
                        onToggleExpand(node._id);
                    }
                }}
            >
                {hasChildren ? (
                    <ChevronIcon
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(node._id);
                        }}
                    >
                        {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </ChevronIcon>
                ) : (
                    <NarrowIcon>
                        <SpacerBox />
                    </NarrowIcon>
                )}
                <NarrowIcon>
                    <Tooltip title={TYPE_HINT[entityType] || entityType}>
                        <EntityIcon fontSize="small" color={entityType === "Client" ? "primary" : "action"} />
                    </Tooltip>
                </NarrowIcon>
                <ListItemText
                    disableTypography
                    primary={
                        <NodeLabelRow>
                            <NodeName variant="body2" $strong={entityType === "Client"}>
                                {node.name}
                            </NodeName>
                            <Chip size="small" variant="outlined" color={TYPE_CHIP_COLOR[entityType] || "default"} label={entityType} />
                            {rateUnset && (
                                <Tooltip title="Overage billing is on, but nobody has chosen what those extra hours are charged at. They are billing at each skill's retainer rate in the meantime. Edit this retainer to confirm the rate.">
                                    <Chip size="small" variant="outlined" color="warning" label="Overage rate not set" />
                                </Tooltip>
                            )}
                            {hiddenRateUnset > 0 && (
                                <Tooltip title={`${hiddenRateUnset} retainer${hiddenRateUnset === 1 ? "" : "s"} under this one have overage billing on with no rate chosen. Expand to see them.`}>
                                    <Chip size="small" variant="outlined" color="warning" label={`${hiddenRateUnset} below need a rate`} />
                                </Tooltip>
                            )}
                        </NodeLabelRow>
                    }
                    secondary={
                        <TermsLine variant="caption">
                            <span>{summarizeTerms(node.billingTerms)}</span>
                            {path.length > 0 && <span>· under {formatPath(path)}</span>}
                        </TermsLine>
                    }
                />
                <Tooltip title={`Edit the retainer terms for this ${entityType.toLowerCase()}`}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(entityType, node, path);
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </NodeRow>
            {hasChildren && (
                <Collapse in={isExpanded}>
                    <List disablePadding>
                        {children.map((child) => (
                            <RetainerRow
                                key={child._id}
                                entityType={childType}
                                node={child}
                                depth={depth + 1}
                                path={childPath}
                                expandedIds={expandedIds}
                                onToggleExpand={onToggleExpand}
                                onEdit={onEdit}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

const BillingRetainers = ({ tabValue }) => {
    const dispatch = useDispatch();
    const [data, setData] = useState({ clients: [] });
    const [loading, setLoading] = useState(false);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [addMenuAnchor, setAddMenuAnchor] = useState(null);
    const [addEntityType, setAddEntityType] = useState(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const loadRetainers = useCallback(() => {
        setLoading(true);
        dispatch(
            getBillingRetainers((resp) => {
                setLoading(false);
                setData(resp || { clients: [] });
            })
        );
    }, [dispatch]);

    useEffect(() => {
        if (tabValue === "retainers") loadRetainers();
    }, [tabValue, loadRetainers]);

    const handleToggleExpand = useCallback((id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleEdit = useCallback((entityType, node, path) => {
        setEditTarget({ entityType, node, path: path || [] });
        setEditDialogOpen(true);
    }, []);

    const handleReload = () => {
        loadRetainers();
    };

    if (tabValue !== "retainers") return null;

    const clientCount = data.clients.length;
    const totalCount = data.clients.reduce(
        (sum, c) => sum + 1 + (c.accounts || []).reduce((accSum, a) => accSum + 1 + (a.groups?.length || 0), 0),
        0
    );
    const groupCount = data.clients.reduce((sum, c) => sum + (c.accounts || []).reduce((accSum, a) => accSum + (a.groups?.length || 0), 0), 0);
    const isLoadingFresh = loading && data.clients.length === 0;

    return (
        <StyledTabPanel value="retainers">
            <Card variant="outlined">
                <CardHeader
                    title={<Typography variant="h6">Billing Retainers</Typography>}
                    subheader="Retainer pools set on a client, an account or a group, shown in the order they are billed. Expand a row to see what sits underneath it."
                    action={
                        <>
                            <Tooltip title="Start a retainer pool on a client, an account or a group. Pick the level the retainer should be billed at.">
                                <Button
                                    size="small"
                                    endIcon={<ExpandMore />}
                                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                                >
                                    Add Retainer
                                </Button>
                            </Tooltip>
                            <Menu
                                anchorEl={addMenuAnchor}
                                open={Boolean(addMenuAnchor)}
                                onClose={() => setAddMenuAnchor(null)}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setAddEntityType("Client");
                                        setAddDialogOpen(true);
                                        setAddMenuAnchor(null);
                                    }}
                                >
                                    Client
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setAddEntityType("Account");
                                        setAddDialogOpen(true);
                                        setAddMenuAnchor(null);
                                    }}
                                >
                                    Account
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setAddEntityType("Group");
                                        setAddDialogOpen(true);
                                        setAddMenuAnchor(null);
                                    }}
                                >
                                    Group
                                </MenuItem>
                            </Menu>
                        </>
                    }
                />
                <CardContent>
                    <StatsLine>
                        {totalCount} retainer {totalCount === 1 ? "entity" : "entities"} across {clientCount} client{clientCount === 1 ? "" : "s"}
                        {groupCount > 0 ? `, including ${groupCount} group ${groupCount === 1 ? "retainer" : "retainers"}` : ""}
                    </StatsLine>

                    {isLoadingFresh ? (
                        <EmptyState>
                            <CircularProgress size={20} />
                        </EmptyState>
                    ) : data.clients.length === 0 ? (
                        <EmptyState>
                            <Typography variant="body2">
                                No retainers are set up yet. Use Add Retainer to start one on a client, an account or a group.
                            </Typography>
                        </EmptyState>
                    ) : (
                        <TreeContainer>
                            <List disablePadding>
                                {data.clients.map((client) => (
                                    <RetainerRow
                                        key={client._id}
                                        entityType="Client"
                                        node={client}
                                        depth={0}
                                        path={[]}
                                        expandedIds={expandedIds}
                                        onToggleExpand={handleToggleExpand}
                                        onEdit={handleEdit}
                                    />
                                ))}
                            </List>
                        </TreeContainer>
                    )}
                </CardContent>
            </Card>

            <EditRetainerDialog
                open={editDialogOpen}
                entityType={editTarget?.entityType}
                node={editTarget?.node}
                path={editTarget?.path}
                onClose={() => setEditDialogOpen(false)}
                onSaved={handleReload}
            />

            <AddRetainerDialog
                open={addDialogOpen}
                entityType={addEntityType}
                onClose={() => setAddDialogOpen(false)}
                onSaved={handleReload}
            />
        </StyledTabPanel>
    );
};

export default BillingRetainers;
